import React, { useState, useEffect } from 'react';

import {
    Box, Typography, Card, CardContent, Grid, Button, Divider, Chip,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    Paper, Table, TableBody, TableCell, TableContainer, TableRow, Alert, TextField, IconButton, CardActions, Avatar
} from '@mui/material';
import {
    ArrowBack, Check, Close, Visibility, Save, Cancel, HomeWork,
    Business, Person, Phone, Email, Description, OpenInNew, PhotoLibrary, AttachMoney, StarRate, Star, Photo
} from '@mui/icons-material';
import {useNavigate, useParams} from "react-router-dom";
import axios from "axios";

// API 응답 타입 정의 - 실제 백엔드 응답에 맞춤
interface ApiBusinessRoomDetail {
    id: number;
    title: string;
    short_description: string;
    address: string;
    address_detail: string;
    accommodation_type: string;
    building_type: string;
    floor_area: number | null;
    floor: number | null;
    has_elevator: boolean;
    has_parking: boolean;
    day_price: string;
    week_price: string;
    month_price: string;
    deposit: string;
    maintenance_fee: string;
    cleaning_fee: string;
    thumbnail_url: string | null;
    detail_urls: string[];
    room_status: string;
    room_type: string;
    is_verified: boolean;
    verified_at: string;
    created_at: string;
    updated_at: string;
    is_confirmed: boolean;
    is_rejected: boolean;
    is_active: boolean;
    host: {
        id: number;
        name: string;
        phone: string;
        email: string;
    };
    BusinessRoom: {
        business_name: string;
        business_number: string;
        business_representative: string;
        business_address: string;
        business_additional_address: string | null;
        business_license_type: string | null;
        business_license_file: string | null;
        business_identification_file: string | null;
        created_at: string;
    };
    room_id: number;
    host_id: number;
    host_name: string;
    host_phone: string;
    host_email: string;
    business_name: string;
    business_number: string;
    business_representative: string;
    business_address: string;
    business_additional_address: string | null;
    business_license_type: string | null;
    business_license_file: string | null;
    business_identification_file: string | null;
    business_registered_at: string;
    room_status_label: string;
    room_type_label: string;
}

// 컴포넌트에서 사용할 인터페이스
interface BusinessRoomDetail {
    id: string;
    title: string;
    shortDescription: string;
    address: string;
    addressDetail: string;
    accommodationType: string;
    buildingType: string;
    floorArea: number | null;
    floor: number | null;
    hasElevator: boolean;
    hasParking: boolean;
    dayPrice: string;
    weekPrice: string;
    monthPrice: string;
    deposit: string;
    maintenanceFee: string;
    cleaningFee: string;
    thumbnailUrl: string | null;
    detailUrls: string[];
    roomType: string;
    roomStatus: string;
    isVerified: boolean;
    verifiedAt: string;
    createdAt: string;
    updatedAt: string;
    hostId: string;
    hostName: string;
    hostPhone: string;
    hostEmail: string;
    businessName: string;
    businessNumber: string;
    businessRepresentative: string;
    businessAddress: string;
    businessAdditionalAddress: string | null;
    businessLicenseType: string | null;
    businessLicenseFile: string | null;
    businessIdentificationFile: string | null;
    businessRegisteredAt: string;
    isConfirmed: boolean;
    isRejected: boolean;
    isActive: boolean;
}

// API 응답 데이터를 컴포넌트 인터페이스로 변환하는 함수
const transformApiToInterface = (apiData: ApiBusinessRoomDetail): BusinessRoomDetail => {
    return {
        id: apiData.id.toString(),
        title: apiData.title,
        shortDescription: apiData.short_description || '',
        address: apiData.address || '',
        addressDetail: apiData.address_detail || '',
        accommodationType: apiData.accommodation_type || '',
        buildingType: apiData.building_type || '',
        floorArea: apiData.floor_area,
        floor: apiData.floor,
        hasElevator: apiData.has_elevator,
        hasParking: apiData.has_parking,
        dayPrice: apiData.day_price || '',
        weekPrice: apiData.week_price || '',
        monthPrice: apiData.month_price || '',
        deposit: apiData.deposit || '',
        maintenanceFee: apiData.maintenance_fee || '',
        cleaningFee: apiData.cleaning_fee || '',
        thumbnailUrl: apiData.thumbnail_url,
        detailUrls: apiData.detail_urls || [],
        roomType: apiData.room_type_label || apiData.room_type,
        roomStatus: apiData.room_status_label || apiData.room_status,
        isVerified: apiData.is_verified,
        verifiedAt: apiData.verified_at || '',
        createdAt: apiData.created_at || '',
        updatedAt: apiData.updated_at || '',
        hostId: apiData.host_id.toString(),
        hostName: apiData.host_name,
        hostPhone: apiData.host_phone || '',
        hostEmail: apiData.host_email || '',
        businessName: apiData.business_name || '',
        businessNumber: apiData.business_number || '',
        businessRepresentative: apiData.business_representative || '',
        businessAddress: apiData.business_address || '',
        businessAdditionalAddress: apiData.business_additional_address || null,
        businessLicenseType: apiData.business_license_type || null,
        businessLicenseFile: apiData.business_license_file || null,
        businessIdentificationFile: apiData.business_identification_file || null,
        businessRegisteredAt: apiData.business_registered_at || '',
        isConfirmed: apiData.is_confirmed || false,
        isRejected: apiData.is_rejected || false,
        isActive: apiData.is_active || false,
    };
};

// 상태 값에 따른 표시 텍스트와 색상 설정 함수
const getRoomStatusLabel = (
    isConfirmed: boolean,
    isRejected: boolean,
    isActive: boolean
): string => {
    if (isRejected) return '승인 거절';
    if (!isConfirmed) return '승인 대기';
    return isActive ? '활성화' : '비활성화';
};

const getRoomStatusColor = (
    isConfirmed: boolean,
    isRejected: boolean,
    isActive: boolean
): 'error' | 'warning' | 'success' | 'default' => {
    if (isRejected) return 'error';      // 거절 → 빨간색
    if (!isConfirmed) return 'warning';  // 승인 대기 → 주황색
    return isActive ? 'success' : 'default'; // 활성화/비활성화
};


const BusinessRoomDetail = () => {
    const { roomId } = useParams<{ roomId: string }>();
    const [showRejectReason, setShowRejectReason] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const navigate = useNavigate();

    const [room, setRoom] = useState<BusinessRoomDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // 이미지 모달 상태
    const [imageDialog, setImageDialog] = useState(false);
    const [currentImage, setCurrentImage] = useState('');
    const [currentImageTitle, setCurrentImageTitle] = useState('');

    //API
    const approveRoom = async () => {
        try {
            const response = await axios.post(`https://roomi.co.kr/api/admin/business-rooms/approve?roomId=${roomId}`);
            alert('승인 완료되었습니다.');
            setRoom({ ...room!, isVerified: true }); // 상태 갱신
            window.location.reload(); // 🔁 새로고침
        } catch (error: any) {
            console.error('승인 요청 실패:', error);
            alert('승인에 실패했습니다.');
        }
    };

    const rejectRoom = async () => {
        if (!rejectReason.trim()) return;

        try {
            const response = await axios.post(
                `https://roomi.co.kr/api/admin/business-rooms/reject?roomId=${roomId}&reason=${encodeURIComponent(rejectReason)}`
            );
            alert('반려 완료되었습니다.');
            window.location.reload(); // 🔁 새로고침
            // navigate('/admin/business-rooms');
        } catch (error: any) {
            console.error('반려 요청 실패:', error);
            alert('반려에 실패했습니다.');
        }
    };
    // 테스트 데이터 (실제 API 응답과 구조 동일)
    const sampleData = {
        id: 50098,
        title: "홍제동",
        short_description: "",
        address: "서울특별시 서대문구 간호대로 3",
        address_detail: "123",
        accommodation_type: "사업자 신고 완료 공간",
        building_type: "오피스텔",
        floor_area: null,
        floor: null,
        has_elevator: true,
        has_parking: true,
        day_price: "₩50,000",
        week_price: "₩666,600",
        month_price: "₩0",
        deposit: "₩0",
        maintenance_fee: "₩0",
        cleaning_fee: "₩0",
        thumbnail_url: null,
        detail_urls: [
            "https://roomi.co.kr/image/room50098_1.jpg",
            "https://roomi.co.kr/image/room50098_2.jpg",
            "https://roomi.co.kr/image/room50098_3.jpg",
            "https://roomi.co.kr/image/room50098_4.jpg",
            "https://roomi.co.kr/image/room50098_5.jpg"
        ],
        room_status: "PENDING",
        room_type: "LODGE",
        is_verified: false,
        verified_at: "",
        created_at: "2025-04-02",
        updated_at: "2025-04-02",
        host: {
            id: 17,
            name: "관리자",
            phone: "",
            email: "admin"
        },
        BusinessRoom: {
            business_name: "ㅇㅇ",
            business_number: "123456786",
            business_representative: "123",
            business_address: "서울특별시 서대문구 간호대로 3",
            business_additional_address: null,
            business_license_type: null,
            business_license_file: "https://roomi.co.kr/image/room_business50098_1.jpg",
            business_identification_file: null,
            created_at: "2025-04-02T02:59:12.114Z"
        },
        room_id: 50098,
        host_id: 17,
        host_name: "관리자",
        host_phone: "",
        host_email: "admin",
        business_name: "ㅇㅇ",
        business_number: "123456786",
        business_representative: "123",
        business_address: "서울특별시 서대문구 간호대로 3",
        business_additional_address: null,
        business_license_type: null,
        business_license_file: "https://roomi.co.kr/image/room_business50098_1.jpg",
        business_identification_file: null,
        business_registered_at: "2025-04-02",
        room_status_label: "PENDING",
        room_type_label: "LODGE"
    };

    // 샘플 데이터 로드 함수 (테스트용)
    const loadSampleData = () => {
        const transformedData = transformApiToInterface(sampleData as ApiBusinessRoomDetail);
        setRoom(transformedData);
        setLoading(false);
        setError('');
    };

    // 데이터 불러오기
    useEffect(() => {
        const fetchRoomDetail = async () => {
            try {
                setLoading(true);
                console.log(`API 요청 URL: https://roomi.co.kr/api/admin/business-rooms/${roomId}`);

                const response = await axios.get(`https://roomi.co.kr/api/admin/business-rooms/${roomId}`);
                console.log('API 응답:', response.data);

                if (response.data.success) {
                    // API 응답 데이터를 컴포넌트 인터페이스로 변환
                    const transformedData = transformApiToInterface(response.data.data);
                    console.log('변환된 데이터:', transformedData);
                    setRoom(transformedData);
                    setError('');
                } else {
                    setError(response.data.message || '방 상세 정보를 불러오는데 실패했습니다.');
                }
            } catch (err: any) {
                console.error('방 상세 정보 로딩 실패:', err);
                setError(`서버에서 데이터를 불러오는데 실패했습니다. 오류: ${err.message || JSON.stringify(err)}`);
            } finally {
                setLoading(false);
            }
        };

        if (roomId) {
            fetchRoomDetail();
        }
    }, [roomId]);

    // 인증 상태 업데이트 (실제 구현 시 API 호출 필요)
    const handleVerification = async (verify: boolean) => {
        alert(`${verify ? '인증 승인' : '인증 거부'} 기능은 아직 구현되지 않았습니다.`);
        // 실제 구현:
        // try {
        //   const response = await axios.post(`/api/admin/business-rooms/${roomId}/verify`, {
        //     verify
        //   });
        //
        //   if (response.data.success) {
        //     setRoom({ ...room!, isVerified: verify });
        //   }
        // } catch (err) {
        //   console.error('인증 상태 업데이트 실패:', err);
        // }
    };

    // 이미지 모달 열기
    const openImageModal = (url: string, title: string) => {
        setCurrentImage(url);
        setCurrentImageTitle(title);
        setImageDialog(true);
    };

    // 목록으로 돌아가기
    const goBack = () => {
        navigate('/admin/business-rooms');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !room) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error || '방 정보를 찾을 수 없습니다.'}</Alert>
                <Button startIcon={<ArrowBack />} onClick={goBack} sx={{ mt: 2 }}>
                    목록으로 돌아가기
                </Button>
                <Button
                    variant="outlined"
                    onClick={loadSampleData}
                    sx={{ mt: 2, ml: 2 }}
                >
                    샘플 데이터 로드
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* 헤더 및 액션 버튼 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button startIcon={<ArrowBack />} onClick={goBack} sx={{ mr: 2 }}>
                        목록
                    </Button>
                    <Typography variant="h5">숙소 상세 정보</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {!room.isVerified ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<Check />}
                                    onClick={approveRoom}
                                >
                                    인증 승인
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Close />}
                                    onClick={() => setShowRejectReason(true)} // ✅ 여기 OK
                                >
                                    인증 거부
                                </Button>
                            </Box>
                            {showRejectReason && (
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="반려 사유를 입력하세요"
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        sx={{ minWidth: 300 }}
                                    />
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={!rejectReason.trim()}
                                        onClick={rejectRoom} // ✅ 요걸로 변경
                                    >
                                        반려 확인
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Chip
                            icon={<Check />}
                            label="인증완료"
                            color="success"
                            variant="outlined"
                        />
                    )}
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* 기본 정보 카드 */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <HomeWork sx={{ mr: 1 }} />
                                <Typography variant="h6">숙소 기본 정보</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell component="th" sx={{ width: '40%', fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>ID</TableCell>
                                            <TableCell>{room.id}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>숙소명</TableCell>
                                            <TableCell>{room.title}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>소개</TableCell>
                                            <TableCell>{room.shortDescription || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>유형</TableCell>
                                            <TableCell>{room.roomType}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>상태</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getRoomStatusLabel(room.isConfirmed, room.isRejected, room.isActive)}
                                                    color={getRoomStatusColor(room.isConfirmed, room.isRejected, room.isActive)}
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>주소</TableCell>
                                            <TableCell>
                                                {room.address || '-'}
                                                {room.addressDetail && <Typography variant="caption" display="block" color="text.secondary">{room.addressDetail}</Typography>}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>건물 유형</TableCell>
                                            <TableCell>{room.buildingType || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>면적</TableCell>
                                            <TableCell>{room.floorArea ? `${room.floorArea}㎡` : '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>층수</TableCell>
                                            <TableCell>{room.floor || '-'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>엘리베이터</TableCell>
                                            <TableCell>{room.hasElevator ? '있음' : '없음'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>주차</TableCell>
                                            <TableCell>{room.hasParking ? '가능' : '불가'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 가격 정보 및 호스트 정보 */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={3}>
                        {/* 가격 정보 */}
                        <Grid size={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <AttachMoney sx={{ mr: 1 }} />
                                        <Typography variant="h6">가격 정보</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />

                                    <TableContainer component={Paper} variant="outlined">
                                        <Table>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell component="th" sx={{ width: '25%', fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>일 요금</TableCell>
                                                    <TableCell>{room.dayPrice ? `${Number(room.dayPrice).toLocaleString()}원` : '-'}</TableCell>
                                                    <TableCell component="th" sx={{ width: '25%', fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>주 요금</TableCell>
                                                    <TableCell>{room.weekPrice ? `${Number(room.weekPrice).toLocaleString()}원` : '-'}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>월 요금</TableCell>
                                                    <TableCell>{room.monthPrice ? `${Number(room.monthPrice).toLocaleString()}원` : '-'}</TableCell>
                                                    <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>보증금</TableCell>
                                                    <TableCell>{room.deposit ? `${Number(room.deposit).toLocaleString()}원` : '-'}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>관리비</TableCell>
                                                    <TableCell>{room.maintenanceFee ? `${Number(room.maintenanceFee).toLocaleString()}원` : '-'}</TableCell>
                                                    <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>청소비</TableCell>
                                                    <TableCell>{room.cleaningFee ? `${Number(room.cleaningFee).toLocaleString()}원` : '-'}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* 호스트 정보 */}
                        <Grid size={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Person sx={{ mr: 1 }} />
                                        <Typography variant="h6">호스트 정보</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />

                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table>
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell component="th" sx={{ width: '40%', fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>이름</TableCell>
                                                            <TableCell>{room.hostName || '-'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>전화번호</TableCell>
                                                            <TableCell>{room.hostPhone || '-'}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>이메일</TableCell>
                                                            <TableCell>{room.hostEmail || '-'}</TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Card variant="outlined" sx={{ width: '100%', backgroundColor: 'rgba(0, 0, 0, 0.02)', p: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60 }}>
                                                            {room.hostName ? room.hostName.charAt(0) : '?'}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="h6">{room.hostName || '호스트'}</Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <StarRate sx={{ color: 'warning.main', fontSize: 18 }} />
                                                                <Typography variant="body2">
                                                                    {room.hostName || '신규 호스트'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Card>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>

                {/* 사업자 정보 */}
                <Grid size={{ xs: 12}}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Business sx={{ mr: 1 }} />
                                <Typography variant="h6">사업자 정보</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {!room.businessName ? (
                                <Alert severity="info">사업자 정보가 등록되지 않았습니다.</Alert>
                            ) : (
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell component="th" sx={{ width: '30%', fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>사업자명</TableCell>
                                                        <TableCell>{room.businessName}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>사업자번호</TableCell>
                                                        <TableCell>{room.businessNumber}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>대표자</TableCell>
                                                        <TableCell>{room.businessRepresentative || '-'}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>주소</TableCell>
                                                        <TableCell>
                                                            {room.businessAddress || '-'}
                                                            {room.businessAdditionalAddress && (
                                                                <Typography variant="caption" display="block" color="text.secondary">
                                                                    {room.businessAdditionalAddress}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>등록 구분</TableCell>
                                                        <TableCell>{room.businessLicenseType || '-'}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell component="th" sx={{ fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.03)' }}>등록일</TableCell>
                                                        <TableCell>{room.businessRegisteredAt || '-'}</TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>등록 서류</Typography>

                                        <Grid container spacing={2}>
                                            {room.businessLicenseFile ? (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Card variant="outlined">
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Description sx={{ mr: 1 }} />
                                                                    <Typography>사업자등록증</Typography>
                                                                </Box>
                                                            </Box>
                                                            {/* 미리보기 이미지 */}
                                                            <Box sx={{ position: 'relative', pt: '56.25%', mb: 1 }}>
                                                                <img
                                                                    src={room.businessLicenseFile}
                                                                    alt="사업자등록증"
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        left: 0,
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover',
                                                                        cursor: 'pointer',
                                                                        borderRadius: '4px'
                                                                    }}
                                                                    onClick={() => openImageModal(room.businessLicenseFile!, '사업자등록증')}
                                                                />
                                                            </Box>
                                                            <Button
                                                                variant="outlined"
                                                                fullWidth
                                                                onClick={() => openImageModal(room.businessLicenseFile!, '사업자등록증')}
                                                            >
                                                                크게 보기
                                                            </Button>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ) : (
                                                <Grid size={{ xs: 12, sm: 8 }}>
                                                    <Card variant="outlined">
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Alert severity="warning">사업자등록증 이미지가 없습니다.</Alert>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            )}

                                            {room.businessIdentificationFile ? (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Card variant="outlined">
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Description sx={{ mr: 1 }} />
                                                                    <Typography>대표자 신분증</Typography>
                                                                </Box>
                                                            </Box>
                                                            {/* 미리보기 이미지 */}
                                                            <Box sx={{ position: 'relative', pt: '56.25%', mb: 1 }}>
                                                                <img
                                                                    src={room.businessIdentificationFile}
                                                                    alt="대표자 신분증"
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        left: 0,
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover',
                                                                        cursor: 'pointer',
                                                                        borderRadius: '4px'
                                                                    }}
                                                                    onClick={() => openImageModal(room.businessIdentificationFile!, '대표자 신분증')}
                                                                />
                                                            </Box>
                                                            <Button
                                                                variant="outlined"
                                                                fullWidth
                                                                onClick={() => openImageModal(room.businessIdentificationFile!, '대표자 신분증')}
                                                            >
                                                                크게 보기
                                                            </Button>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ) : (
                                                <Grid size={{ xs: 12, sm: 6 }}>
                                                    <Card variant="outlined">
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Alert severity="warning">대표자 신분증 이미지가 없습니다.</Alert>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* 숙소 이미지 */}
                {(room.thumbnailUrl || (room.detailUrls && room.detailUrls.length > 0)) && (
                    <Grid size={{ xs: 12}}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PhotoLibrary sx={{ mr: 1 }} />
                                    <Typography variant="h6">숙소 이미지</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={2}>
                                    {room.thumbnailUrl && (
                                        <Grid size={{ xs: 12, md: 4, sm:6 }}>
                                            <Card variant="outlined">
                                                <CardContent sx={{ p: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1 }}>
                                                        <Star sx={{ mr: 1, color: 'warning.main', fontSize: 18 }} />
                                                        <Typography variant="subtitle2" fontWeight="bold">대표 이미지</Typography>
                                                    </Box>
                                                    <Box sx={{ position: 'relative', pt: '75%' /* 4:3 Aspect Ratio */ }}>
                                                        <img
                                                            src={room.thumbnailUrl}
                                                            alt="대표 이미지"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => openImageModal(room.thumbnailUrl!, '대표 이미지')}
                                                        />
                                                    </Box>
                                                </CardContent>
                                                <CardActions sx={{ p: 1, pt: 0 }}>
                                                    <Button
                                                        fullWidth
                                                        size="small"
                                                        onClick={() => openImageModal(room.thumbnailUrl!, '대표 이미지')}
                                                    >
                                                        크게 보기
                                                    </Button>
                                                </CardActions>
                                            </Card>
                                        </Grid>
                                    )}

                                    {room.detailUrls && room.detailUrls.map((url, index) => (
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                                            <Card variant="outlined">
                                                <CardContent sx={{ p: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1 }}>
                                                        <Photo sx={{ mr: 1, color: 'info.main', fontSize: 18 }} />
                                                        <Typography variant="subtitle2">상세 이미지 {index + 1}</Typography>
                                                    </Box>
                                                    <Box sx={{ position: 'relative', pt: '75%' /* 4:3 Aspect Ratio */ }}>
                                                        <img
                                                            src={url}
                                                            alt={`상세 이미지 ${index + 1}`}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => openImageModal(url, `상세 이미지 ${index + 1}`)}
                                                        />
                                                    </Box>
                                                </CardContent>
                                                <CardActions sx={{ p: 1, pt: 0 }}>
                                                    <Button
                                                        fullWidth
                                                        size="small"
                                                        onClick={() => openImageModal(url, `상세 이미지 ${index + 1}`)}
                                                    >
                                                        크게 보기
                                                    </Button>
                                                </CardActions>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {/* 이미지 모달 */}
            <Dialog open={imageDialog} onClose={() => setImageDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {currentImageTitle}
                        <IconButton onClick={() => setImageDialog(false)} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <img
                            src={currentImage}
                            alt={currentImageTitle}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        startIcon={<OpenInNew />}
                        onClick={() => window.open(currentImage, '_blank')}
                    >
                        원본 보기
                    </Button>
                    <Button onClick={() => setImageDialog(false)}>닫기</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BusinessRoomDetail;